import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GoogleIcon from '@mui/icons-material/Google';
import { v4 as uuidv4 } from 'uuid';
import validator from 'validator';
import { useMatch } from '../../contexts/MatchContext';
import { useAuth } from '../../auth/AuthContext';
import * as api from '../../api';
import CountrySelect from '../../components/CountrySelect';
import { isNativeApp, requestGoogleLogin } from '../../utils/nativeApp';
import Snackbar from '../../components/Snackbar';
import ShooterForm, { toDivisionKey, toCategoryKey } from '../../components/ShooterForm';

// ---------------------------------------------------------------------------
// Landing screen – shown before registration form
// ---------------------------------------------------------------------------
function GoogleSignInButton({ onLogin }) {
  const ref = React.useRef(null);
  useEffect(() => {
    if (!ref.current || !window.google?.accounts?.id) return;
    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      callback: onLogin,
    });
    window.google.accounts.id.renderButton(ref.current, {
      type: 'standard',
      size: 'large',
      width: 280,
      text: 'signin_with',
    });
  }, [onLogin]);
  return <div ref={ref} />;
}

function RegistrationLanding({ t, match, onRegisterNoAuth, onLoginGoogle, onLoginCognito, onSignUpCognito, onManagePin, onForceChangePassword, onForgotPassword, onConfirmForgotPassword }) {
  const isPeriodOverCanPatch = match?.registration === 'period-over-can-patch';
  const [cognitoMode, setCognitoMode] = useState(null); // null | 'signin' | 'signup'
  const [showPin, setShowPin] = useState(false);
  const [cognitoEmail, setCognitoEmail] = useState('');
  const [cognitoPassword, setCognitoPassword] = useState('');
  const [cognitoPasswordConfirm, setCognitoPasswordConfirm] = useState('');
  const [cognitoError, setCognitoError] = useState(null);
  const [cognitoLoading, setCognitoLoading] = useState(false);
  const [pinCountryCode, setPinCountryCode] = useState('');
  const [pinPublicId, setPinPublicId] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [forgotPinMode, setForgotPinMode] = useState(false);
  const [forgotPinEmail, setForgotPinEmail] = useState('');
  const [forgotPinLoading, setForgotPinLoading] = useState(false);
  const [forgotPinMessage, setForgotPinMessage] = useState(null); // { type: 'success'|'error', text }
  const [forceChangeSession, setForceChangeSession] = useState(null);
  const [forceChangeEmail, setForceChangeEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [forgotMode, setForgotMode] = useState(null); // null | 'request' | 'confirm'
  const [resetCode, setResetCode] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const validatePassword = (pw) => {
    if (pw.length < 8) return t('regLanding.passwordPolicy');
    if (!/[a-z]/.test(pw)) return t('regLanding.passwordPolicy');
    if (!/[A-Z]/.test(pw)) return t('regLanding.passwordPolicy');
    if (!/[0-9]/.test(pw)) return t('regLanding.passwordPolicy');
    if (!/[^a-zA-Z0-9]/.test(pw)) return t('regLanding.passwordPolicy');
    return null;
  };

  const handleCognitoLogin = async () => {
    if (!cognitoEmail || !cognitoPassword) return;
    if (!validator.isEmail(cognitoEmail)) { setCognitoError(t('auth.emailError')); return; }
    setCognitoLoading(true);
    setCognitoError(null);
    try {
      await onLoginCognito({ email: cognitoEmail, password: cognitoPassword });
    } catch (err) {
      if (err.challenge === 'NEW_PASSWORD_REQUIRED') {
        setForceChangeSession(err.session);
        setForceChangeEmail(err.email || cognitoEmail);
        setCognitoError(null);
      } else if (err.challenge === 'PASSWORD_RESET_REQUIRED') {
        try { await onForgotPassword({ email: cognitoEmail }); } catch (_) { /* ignore */ }
        setForgotMode('confirm');
        setForceChangeEmail(cognitoEmail);
        setForgotSuccess(t('auth.resetCodeSent'));
        setCognitoError(null);
      } else {
        const status = err?.response?.status;
        if (status === 401 || status === 404) {
          setCognitoError(t('auth.error'));
        } else {
          setCognitoError(t('common.saveError'));
        }
      }
    } finally {
      setCognitoLoading(false);
    }
  };

  const handleForceChange = async () => {
    if (!newPassword) { setCognitoError(t('auth.setNewPassword')); return; }
    if (newPassword !== newPasswordConfirm) { setCognitoError(t('auth.passwordsDoNotMatch')); return; }
    setCognitoLoading(true);
    setCognitoError(null);
    try {
      await onForceChangePassword({ email: forceChangeEmail, newPassword, session: forceChangeSession });
    } catch (err) {
      setCognitoError(err.response?.data?.detail || t('common.saveError'));
    } finally {
      setCognitoLoading(false);
    }
  };

  const handleForgotRequest = async () => {
    if (!cognitoEmail) { setCognitoError(t('auth.emailError')); return; }
    if (!validator.isEmail(cognitoEmail)) { setCognitoError(t('auth.emailError')); return; }
    setCognitoLoading(true);
    setCognitoError(null);
    try {
      await onForgotPassword({ email: cognitoEmail });
      setForgotMode('confirm');
      setForgotSuccess(t('auth.resetCodeSent'));
    } catch (err) {
      setCognitoError(err.response?.data?.detail || t('common.saveError'));
    } finally {
      setCognitoLoading(false);
    }
  };

  const handleForgotConfirm = async () => {
    if (!resetCode || !newPassword) { setCognitoError(t('auth.verificationCode')); return; }
    if (newPassword !== newPasswordConfirm) { setCognitoError(t('auth.passwordsDoNotMatch')); return; }
    setCognitoLoading(true);
    setCognitoError(null);
    try {
      await onConfirmForgotPassword({ email: cognitoEmail, code: resetCode, newPassword });
    } catch (err) {
      setCognitoError(err.response?.data?.detail || t('common.saveError'));
    } finally {
      setCognitoLoading(false);
    }
  };

  const handleCognitoSignUp = async () => {
    if (!cognitoEmail || !cognitoPassword) return;
    if (!validator.isEmail(cognitoEmail)) { setCognitoError(t('auth.emailError')); return; }
    const pwError = validatePassword(cognitoPassword);
    if (pwError) { setCognitoError(pwError); return; }
    if (cognitoPassword !== cognitoPasswordConfirm) { setCognitoError(t('regLanding.passwordMismatch')); return; }
    setCognitoLoading(true);
    setCognitoError(null);
    try {
      await onSignUpCognito({ email: cognitoEmail, password: cognitoPassword });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409) {
        setCognitoError(t('regLanding.userExists'));
      } else if (status === 400 && detail.includes('Password')) {
        setCognitoError(t('regLanding.passwordPolicy'));
      } else {
        setCognitoError(t('common.saveError'));
      }
    } finally {
      setCognitoLoading(false);
    }
  };

  const handlePinLogin = async () => {
    if (!pinPublicId || !pin) {
      setPinError(true);
      return;
    }
    setPinLoading(true);
    setPinError(null);
    try {
      await onManagePin({ countryCode: pinCountryCode, publicId: pinPublicId, pin });
    } catch {
      setPinError(true);
    } finally {
      setPinLoading(false);
    }
  };

  const handleForgotPin = async () => {
    if (!pinPublicId || !forgotPinEmail) return;
    setForgotPinLoading(true);
    setForgotPinMessage(null);
    try {
      const fullPublicId = pinCountryCode ? `${pinCountryCode}|${pinPublicId}` : pinPublicId;
      await api.forgotPin(match.id, { publicId: fullPublicId, email: forgotPinEmail });
      setForgotPinMessage({ type: 'success', text: t('regLanding.forgotPinSuccess') });
    } catch {
      setForgotPinMessage({ type: 'error', text: t('regLanding.forgotPinError') });
    } finally {
      setForgotPinLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, textAlign: 'center' }}>
      {isPeriodOverCanPatch && (
        <Alert severity="info" sx={{ mb: 2, textAlign: 'start' }}>
          {t('regLanding.registrationPeriodOverCanPatch', 'Registration is closed. Existing shooters can still update their details. Log in below to edit your registration.')}
        </Alert>
      )}
      <Typography variant="h4" gutterBottom>
        {t('regLanding.title')}
      </Typography>
      {!isPeriodOverCanPatch && (
        <Typography variant="h6" sx={{ mb: 3 }}>
          {t('regLanding.intro')}
        </Typography>
      )}

      {/* Google Sign-In */}
      {isNativeApp() ? (
        <Button
          variant="contained"
          size="large"
          startIcon={<GoogleIcon />}
          sx={{ width: 280, height: 50, textTransform: 'none', mb: 1, gap: 1 }}
          onClick={() => {
            sessionStorage.setItem('nativeAuthReturnTo', window.location.pathname);
            requestGoogleLogin();
          }}
        >
          {t('auth.signInWithGoogle')}
        </Button>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <GoogleSignInButton onLogin={onLoginGoogle} />
        </Box>
      )}

      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
        {t('regLanding.socialNote')}
      </Typography>

      {/* EndOfScoring / Cognito Login & Sign Up */}
      {!cognitoMode && !forceChangeSession && !forgotMode ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            sx={{ width: 280, height: 50, textTransform: 'none' }}
            onClick={() => setCognitoMode('signin')}
          >
            {t('regLanding.loginEndOfScoring')}
          </Button>
          <Button
            variant="text"
            sx={{ textTransform: 'none' }}
            onClick={() => setCognitoMode('signup')}
          >
            {t('regLanding.signUpEndOfScoring')}
          </Button>
        </Box>
      ) : forceChangeSession ? (
        <Paper sx={{ p: 2, mb: 2, maxWidth: 320, mx: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('auth.setNewPassword')}
          </Typography>
          <Alert severity="info" sx={{ mb: 1 }}>{t('auth.newPasswordRequired')}</Alert>
          <TextField
            label={t('auth.setNewPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label={t('auth.confirmNewPassword')}
            type="password"
            value={newPasswordConfirm}
            onChange={(e) => { setNewPasswordConfirm(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleForceChange(); }}
          />
          {cognitoError && <Alert severity="error" sx={{ mb: 1 }}>{cognitoError}</Alert>}
          <Button
            variant="contained"
            fullWidth
            onClick={handleForceChange}
            disabled={cognitoLoading}
            sx={{ mb: 1 }}
          >
            {cognitoLoading ? <CircularProgress size={20} /> : t('auth.setNewPassword')}
          </Button>
        </Paper>
      ) : forgotMode === 'request' ? (
        <Paper sx={{ p: 2, mb: 2, maxWidth: 320, mx: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('auth.forgotPasswordTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('auth.forgotPasswordDescription')}</Typography>
          <TextField
            label={t('auth.email')}
            value={cognitoEmail}
            onChange={(e) => { setCognitoEmail(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleForgotRequest(); }}
          />
          {cognitoError && <Alert severity="error" sx={{ mb: 1 }}>{cognitoError}</Alert>}
          <Button
            variant="contained"
            fullWidth
            onClick={handleForgotRequest}
            disabled={cognitoLoading}
            sx={{ mb: 1 }}
          >
            {cognitoLoading ? <CircularProgress size={20} /> : t('auth.sendResetCode')}
          </Button>
          <Button
            variant="text"
            size="small"
            fullWidth
            sx={{ textTransform: 'none' }}
            onClick={() => { setForgotMode(null); setCognitoMode('signin'); setCognitoError(null); }}
          >
            {t('auth.backToSignIn')}
          </Button>
        </Paper>
      ) : forgotMode === 'confirm' ? (
        <Paper sx={{ p: 2, mb: 2, maxWidth: 320, mx: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('auth.resetPasswordTitle')}
          </Typography>
          {forgotSuccess && <Alert severity="success" sx={{ mb: 1 }}>{forgotSuccess}</Alert>}
          <TextField
            label={t('auth.verificationCode')}
            value={resetCode}
            onChange={(e) => { setResetCode(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label={t('auth.setNewPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label={t('auth.confirmNewPassword')}
            type="password"
            value={newPasswordConfirm}
            onChange={(e) => { setNewPasswordConfirm(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleForgotConfirm(); }}
          />
          {cognitoError && <Alert severity="error" sx={{ mb: 1 }}>{cognitoError}</Alert>}
          <Button
            variant="contained"
            fullWidth
            onClick={handleForgotConfirm}
            disabled={cognitoLoading}
            sx={{ mb: 1 }}
          >
            {cognitoLoading ? <CircularProgress size={20} /> : t('auth.resetPassword')}
          </Button>
          <Button
            variant="text"
            size="small"
            fullWidth
            sx={{ textTransform: 'none' }}
            onClick={() => { setForgotMode(null); setCognitoMode('signin'); setCognitoError(null); setForgotSuccess(''); }}
          >
            {t('auth.backToSignIn')}
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 2, maxWidth: 320, mx: 'auto' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {cognitoMode === 'signin' ? t('auth.signIn') : t('auth.signUp')}
          </Typography>
          <TextField
            label={t('auth.email')}
            value={cognitoEmail}
            onChange={(e) => { setCognitoEmail(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
          />
          <TextField
            label={t('auth.password')}
            type="password"
            value={cognitoPassword}
            onChange={(e) => { setCognitoPassword(e.target.value); setCognitoError(null); }}
            fullWidth
            size="small"
            sx={{ mb: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter' && cognitoMode === 'signin') handleCognitoLogin(); }}
          />
          {cognitoMode === 'signup' && (
            <TextField
              label={t('regLanding.confirmPassword')}
              type="password"
              value={cognitoPasswordConfirm}
              onChange={(e) => { setCognitoPasswordConfirm(e.target.value); setCognitoError(null); }}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCognitoSignUp(); }}
            />
          )}
          {cognitoError && <Alert severity="error" sx={{ mb: 1 }}>{cognitoError}</Alert>}
          <Button
            variant="contained"
            fullWidth
            onClick={cognitoMode === 'signin' ? handleCognitoLogin : handleCognitoSignUp}
            disabled={cognitoLoading}
            sx={{ mb: 1 }}
          >
            {cognitoLoading ? <CircularProgress size={20} /> : (cognitoMode === 'signin' ? t('auth.signIn') : t('auth.signUp'))}
          </Button>
          {cognitoMode === 'signin' && (
            <Button
              variant="text"
              size="small"
              fullWidth
              sx={{ textTransform: 'none', mb: 0.5 }}
              onClick={() => { setForgotMode('request'); setCognitoMode(null); setCognitoError(null); }}
            >
              {t('auth.forgotPassword')}
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            fullWidth
            sx={{ textTransform: 'none' }}
            onClick={() => { setCognitoMode(cognitoMode === 'signin' ? 'signup' : 'signin'); setCognitoError(null); }}
          >
            {cognitoMode === 'signin' ? t('regLanding.switchToSignUp') : t('regLanding.switchToSignIn')}
          </Button>
        </Paper>
      )}

      {!isPeriodOverCanPatch && (
        <>
          <Divider sx={{ my: 3 }} />

          {/* Register without login */}
          <Typography variant="h6">
            {t('regLanding.noLoginTitle')}
          </Typography>
          <Button
            variant="text"
            color="primary"
            sx={{ textTransform: 'none', mb: 3 }}
            onClick={onRegisterNoAuth}
          >
            {t('regLanding.noLoginAction')}
          </Button>
        </>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Manage with PIN */}
      <Typography variant="h6" sx={{ mt: 2 }}>
        {t('regLanding.pinTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('regLanding.pinDescription')}
      </Typography>

      {!showPin ? (
        <Button
          variant="text"
          color="primary"
          sx={{ textTransform: 'none' }}
          onClick={() => setShowPin(true)}
        >
          {t('regLanding.pinAction')}
        </Button>
      ) : (
        <Paper sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <CountrySelect
              label={t('regLanding.countryCode')}
              value={pinCountryCode}
              onChange={setPinCountryCode}
              size="small"
              sx={{ minWidth: 160 }}
            />
            <TextField
              label={t('shooter.id')}
              value={pinPublicId}
              onChange={(e) => { setPinPublicId(e.target.value); setPinError(null); setForgotPinMessage(null); }}
              error={pinError && !pinPublicId}
              size="small"
              sx={{ flex: 1, minWidth: 100 }}
            />
            {!forgotPinMode ? (
              <>
                <TextField
                  label={t('regLanding.pinCode')}
                  value={pin}
                  onChange={(e) => { setPin(e.target.value); setPinError(null); }}
                  error={pinError && !pin}
                  size="small"
                  sx={{ width: 100 }}
                />
                <Button variant="contained" size="small" onClick={handlePinLogin} disabled={pinLoading}>
                  {pinLoading ? <CircularProgress size={20} /> : t('auth.signIn')}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  label={t('regLanding.forgotPinEmail')}
                  value={forgotPinEmail}
                  onChange={(e) => { setForgotPinEmail(e.target.value); setForgotPinMessage(null); }}
                  size="small"
                  type="email"
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <Button variant="contained" size="small" onClick={handleForgotPin} disabled={forgotPinLoading}>
                  {forgotPinLoading ? <CircularProgress size={20} /> : t('regLanding.sendPin')}
                </Button>
              </>
            )}
          </Box>
          {forgotPinMessage && (
            <Alert severity={forgotPinMessage.type} sx={{ mt: 1 }}>
              {forgotPinMessage.text}
            </Alert>
          )}
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            {!forgotPinMode ? (
              <Button
                variant="text"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => { setForgotPinMode(true); setForgotPinMessage(null); }}
              >
                {t('regLanding.forgotPin')}
              </Button>
            ) : (
              <Button
                variant="text"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => { setForgotPinMode(false); setForgotPinMessage(null); }}
              >
                {t('regLanding.backToPin')}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// My Registrations screen – shown after login, before registration form
// ---------------------------------------------------------------------------
function MyRegistrations({ t, match, authInfo, onNewRegistration, onEditRegistration, onLogout }) {
  const [shooters, setShooters] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [regMenuAnchor, setRegMenuAnchor] = useState(null);
  const [regMenuShooter, setRegMenuShooter] = useState(null);
  const [confirmCancelShooter, setConfirmCancelShooter] = useState(null);

  useEffect(() => {
    if (!match) return;
    if (authInfo?.provider === 'pin') {
      // PIN auth: re-verify to get fresh data
      api.verifyPin(match.id, { publicId: authInfo.publicId, pin: authInfo.pin }).then((res) => {
        setShooters(res.data || []);
      }).catch(() => setShooters([]));
    } else {
      // Google/Cognito: use authenticated endpoint
      const params = authInfo?.provider === 'google' && authInfo?.token ? { googleToken: authInfo.token } : {};
      api.getMyShooters(match.id, params).then((res) => {
        setShooters(res.data || []);
      }).catch(() => setShooters([]));
    }
  }, [match?.id]);

  if (!shooters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const toDivisionKey = (div) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
      'sg-open': 'sgOpen', 'sg-modified': 'sgModified', 'sg-standard': 'sgStandard',
      'sg-standard-manual': 'sgStandardManual',
    };
    return map[div] || div;
  };

  const toCategoryKey = (cat) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
      'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
    };
    return map[cat] || cat;
  };

  const toPowerFactorKey = (pf) => {
    const map = { 'minor': 'minor', 'major': 'major' };
    return map[pf] || pf;
  };

  const getStatus = (s) => {
    if (s.cancelledParticipation) return <span style={{ color: '#d32f2f' }}>&#x2718; {t('registration.cancelledParticipation')}</span>;
    if (s.cancellationRequest) return <span style={{ color: '#d32f2f' }}>&#x26A0; {t('registration.cancellationPending', 'Cancellation pending')}</span>;
    if (s.verified) return t('registration.verified');
    if (s.paid) return t('registration.paid');
    return t('registration.participating');
  };

  const handleRequestCancel = async (shooter) => {
    setCancellingId(shooter.id);
    try {
      await api.updateShooter(match.id, shooter.id, { cancellationRequest: { timestamp: Date.now() } });
      setShooters((prev) => prev.map((s) => s.id === shooter.id ? { ...s, cancellationRequest: { timestamp: Date.now() } } : s));
    } catch {}
    setCancellingId(null);
  };

  const handleWithdrawCancel = async (shooter) => {
    setCancellingId(shooter.id);
    try {
      await api.updateShooter(match.id, shooter.id, { cancellationRequest: null });
      setShooters((prev) => prev.map((s) => s.id === shooter.id ? { ...s, cancellationRequest: null } : s));
    } catch {}
    setCancellingId(null);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {authInfo && (
          <Typography variant="body2" color="text.secondary">
            {t('regLanding.loggedInAs', { name: authInfo.name || authInfo.email })}
          </Typography>
        )}
        {onLogout && (
          <Button variant="text" size="small" onClick={onLogout} sx={{ textTransform: 'none' }}>
            {t('auth.signOut')}
          </Button>
        )}
      </Box>

      {shooters.length > 0 ? (
        <>
          <Typography variant="h6" gutterBottom>{t('regLanding.myRegistrations')}</Typography>
          <Paper sx={{ mb: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('shooter.id')}</TableCell>
                  <TableCell>{t('shooter.name')}</TableCell>
                  <TableCell>{t('shooter.club')}</TableCell>
                  <TableCell>{t('shooter.division')}</TableCell>
                  <TableCell>{t('shooter.category')}</TableCell>
                  <TableCell>{t('shooter.powerFactor')}</TableCell>
                  <TableCell>{t('matches.squads')}</TableCell>
                  <TableCell>{t('registration.participation')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {shooters.map((s) => {
                  const parts = (s.publicId || '').split('|');
                  const displayId = parts[parts.length - 1];
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>{displayId}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.club || '—'}</TableCell>
                      <TableCell>{t(`divisions.${toDivisionKey(s.division)}`, { defaultValue: s.division })}</TableCell>
                      <TableCell>{t(`categories.${toCategoryKey(s.category)}`, { defaultValue: s.category })}</TableCell>
                      <TableCell>{t(`registration.${toPowerFactorKey(s['power-factor'])}`, { defaultValue: s['power-factor'] })}</TableCell>
                      <TableCell>{(() => { const sq = (match.squads || []).find((q) => q.id === s.squad); return sq ? sq.title : (s.squadTitle || '—'); })()}</TableCell>
                      <TableCell>{getStatus(s)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" onClick={() => onEditRegistration(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {!s.cancelledParticipation && (
                          <IconButton size="small" onClick={(e) => { setRegMenuAnchor(e.currentTarget); setRegMenuShooter(s); }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          {/* More menu */}
          <Menu anchorEl={regMenuAnchor} open={!!regMenuAnchor} onClose={() => { setRegMenuAnchor(null); setRegMenuShooter(null); }}>
            {regMenuShooter && !regMenuShooter.cancellationRequest && (
              <MenuItem onClick={() => { setConfirmCancelShooter(regMenuShooter); setRegMenuAnchor(null); setRegMenuShooter(null); }}>
                {t('registration.requestCancel', 'Request cancellation')}
              </MenuItem>
            )}
            {regMenuShooter && regMenuShooter.cancellationRequest && (
              <MenuItem onClick={() => { handleWithdrawCancel(regMenuShooter); setRegMenuAnchor(null); setRegMenuShooter(null); }}>
                {t('registration.withdrawCancel', 'Withdraw request')}
              </MenuItem>
            )}
          </Menu>

          {/* Confirm cancellation dialog */}
          <Dialog open={!!confirmCancelShooter} onClose={() => setConfirmCancelShooter(null)} maxWidth="xs" fullWidth>
            <DialogTitle>{t('registration.requestCancel', 'Request cancellation')}</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                {t('registration.confirmCancelMsg', 'Are you sure you want to request cancellation of your registration? This request will be sent to the match director for approval.')}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmCancelShooter(null)}>{t('common.cancel')}</Button>
              <Button color="error" variant="contained" disabled={!!cancellingId} onClick={async () => {
                await handleRequestCancel(confirmCancelShooter);
                setConfirmCancelShooter(null);
              }}>
                {t('registration.confirmCancel', 'Yes, request cancellation')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {t('regLanding.noRegistrations')}
        </Typography>
      )}

      {match.registration !== 'period-over-can-patch' && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onNewRegistration}
          sx={{ mt: 1 }}
        >
          {t('regLanding.addNewRegistration')}
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Registration form
// ---------------------------------------------------------------------------
function RegistrationForm({ t, match, authInfo, editingShooter, onBack, registrationCode, ro = false }) {
  const formRef = React.useRef();
  const [matchData, setMatchData] = useState(null);
  // Squads with capacity from the server; the shooter list may be private.
  // undefined = loading, null = unavailable (registration not open for this caller)
  const [squadAvailability, setSquadAvailability] = useState(undefined);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pastRegistrations, setPastRegistrations] = useState([]);
  const [wizardStep, setWizardStep] = useState(null); // null = closed, 'name' | 'publicId' | 'divCat' | 'final'
  const [wizardName, setWizardName] = useState(null);
  const [wizardPublicId, setWizardPublicId] = useState(null);
  const [wizardDivCat, setWizardDivCat] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    api.getMatch(match.id).then((res) => setMatchData(res.data));
    loadSquadAvailability();

    // Fetch past registrations for authenticated users (not editing)
    if (!editingShooter && authInfo?.provider !== 'pin') {
      const params = authInfo?.provider === 'google' && authInfo?.token ? { googleToken: authInfo.token } : {};
      api.getMyShootersAll(params).then((res) => {
        const all = res.data || [];
        setPastRegistrations(all.filter((s) => s.matchId !== match.id));
      }).catch(() => {});
    }
  }, [match?.id]);

  function loadSquadAvailability() {
    const params = { ...(registrationCode ? { registrationCode } : {}), ...(ro ? { ro: true } : {}) };
    return api.getSquadAvailability(match.id, params)
      .then((res) => setSquadAvailability(res.data?.squads || []))
      .catch(() => setSquadAvailability(null));
  }

  const handleSave = async (data) => {
    setSending(true);
    setSaveError(null);

    try {
      // Set user namespace if authenticated via Google
      if (!editingShooter && authInfo?.provider === 'google' && authInfo?.email) {
        data.googleUser = `com.google|${authInfo.email}`;
      }

      if (editingShooter) {
        if (authInfo?.provider === 'pin' && authInfo?.pin) {
          data.pin = `com.endofscoring.pin|${match.id}-${authInfo.pin}`;
        }
        const updateParams = {};
        if (registrationCode) updateParams.registrationCode = registrationCode;
        if (authInfo?.provider === 'google' && authInfo?.token) {
          updateParams.googleToken = authInfo.token;
        }
        await api.updateShooter(match.id, editingShooter.id, data, updateParams);
      } else {
        data.id = uuidv4();
        const registerParams = { ...(registrationCode ? { registrationCode } : {}), ...(ro ? { ro: true } : {}) };
        await api.registerShooter(match.id, data, Object.keys(registerParams).length ? registerParams : undefined);
      }
      setSuccess(true);
      await loadSquadAvailability();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('Squad is full') || detail.includes('squad-full')) {
        setSaveError(t('registration.squadFullError'));
      } else if (detail.includes('publicKey exists') || detail.includes('conflict')) {
        setSaveError(t('registration.userExists'));
      } else if (detail.includes('name-mismatch')) {
        const expectedName = detail.split(':').slice(1).join(':');
        setSaveError(expectedName
          ? t('registration.nameMismatchExpected', { name: expectedName, defaultValue: `Name does not match. Expected: ${expectedName}` })
          : t('registration.nameMismatch', 'Name does not match the authorized list'));
      } else if (detail.includes('authorized list')) {
        setSaveError(t('registration.notInAuthorizedList', 'Shooter not found in authorized list'));
      } else if (detail.includes('not allowed for this shooter')) {
        setSaveError(detail);
      } else if (detail.includes('Club is set from registration sheet')) {
        setSaveError(detail);
      } else {
        setSaveError(detail || t('common.saveError'));
      }
    } finally {
      setSending(false);
    }
  };

  const loadFromPast = (reg) => {
    const { squad, team, ...rest } = reg;
    formRef.current?.loadData(rest);
    setWizardStep(null);
    setWizardName(null);
    setWizardPublicId(null);
    setWizardDivCat(null);
  };

  const resetWizard = (toStep = null) => {
    setWizardStep(toStep);
    if (!toStep || toStep === 'name') { setWizardName(null); setWizardPublicId(null); setWizardDivCat(null); }
    else if (toStep === 'publicId') { setWizardPublicId(null); setWizardDivCat(null); }
    else if (toStep === 'divCat') { setWizardDivCat(null); }
  };

  if (!matchData || squadAvailability === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('regLanding.successMessage')}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="contained" onClick={() => setSuccess(false)}>
            {t('regLanding.registerAnother')}
          </Button>
          {onBack && (
            <Button variant="outlined" onClick={onBack}>
              {t('common.back')}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  const $match = matchData || match;
  const computedIdLocked = editingShooter && (
    match.registration === 'period-over-can-patch'
    || (match.registrationPatchLockedFields || []).includes('publicId')
  );

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2, pb: 4, overflowX: 'hidden' }}>
      {onBack && (
        <Button variant="text" startIcon={document.body.dir === 'rtl' ? <ArrowForwardIcon /> : <ArrowBackIcon />} onClick={onBack} sx={{ mb: 1, textTransform: 'none' }}>
          {t('common.back')}
        </Button>
      )}

      {authInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('regLanding.loggedInAs', { name: authInfo.name || authInfo.email })}
        </Alert>
      )}

      {!editingShooter && pastRegistrations.length > 0 && (() => {
        // IDs already registered here are rejected by the server ("user exists")
        const filtered = pastRegistrations;
        if (filtered.length === 0) return null;

        // Helper: strip part after '-' from the numeric part of the publicId
        const displayPublicId = (publicId) => {
          const parts = (publicId || '').split('|');
          const num = parts.length > 1 ? parts.slice(1).join('|') : publicId;
          return num.split('-')[0];
        };

        // Step 1: unique names
        const uniqueNames = Array.from(new Set(filtered.map((r) => r.name))).sort();

        // Step 2: unique displayed publicIds for the selected name
        const regsForName = wizardName ? filtered.filter((r) => r.name === wizardName) : [];
        const uniquePublicIds = Array.from(new Set(regsForName.map((r) => displayPublicId(r.publicId)))).sort();

        // Step 3: unique div+cat+pf combinations for the selected name + publicId
        const regsForPublicId = wizardPublicId
          ? regsForName.filter((r) => displayPublicId(r.publicId) === wizardPublicId) : [];
        const divCatSeen = new Set();
        const uniqueDivCats = [];
        regsForPublicId.forEach((r) => {
          const key = [r.division, r.category, r['power-factor']].join('|');
          if (divCatSeen.has(key)) return;
          divCatSeen.add(key);
          uniqueDivCats.push({ key, division: r.division, category: r.category, 'power-factor': r['power-factor'] });
        });

        // Step 4: final - unique regs considering current match extra fields
        const matchExtraFields = match.registrationExtraFields || [];
        const regsForDivCat = wizardDivCat
          ? regsForPublicId.filter((r) => [r.division, r.category, r['power-factor']].join('|') === wizardDivCat)
          : [];
        const finalSeen = new Set();
        const finalUniqueRegs = [];
        regsForDivCat.forEach((r) => {
          const relevantFields = matchExtraFields.map((f) => {
            const key = (typeof f === 'string' ? f : f?.key || '').replace(/_optional$/, '');
            return `${key}=${(r.extraFields || {})[key] || ''}`;
          });
          const key = [r.club || '', r.email || '', ...relevantFields].join('||');
          if (finalSeen.has(key)) return;
          finalSeen.add(key);
          finalUniqueRegs.push(r);
        });

        const BackArrowIcon = document.body.dir === 'rtl' ? ArrowForwardIcon : ArrowBackIcon;
        return (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              sx={{ textTransform: 'none' }}
              onClick={() => setWizardStep('name')}
            >
              {t('regLanding.loadFromPast')}
            </Button>
          </Box>

          <Dialog
            open={wizardStep !== null}
            onClose={() => resetWizard(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {wizardStep !== 'name' && wizardStep !== null && (
                <IconButton size="small" onClick={() => {
                  if (wizardStep === 'publicId') resetWizard('name');
                  else if (wizardStep === 'divCat') resetWizard('publicId');
                  else if (wizardStep === 'final') resetWizard('divCat');
                }}>
                  <BackArrowIcon />
                </IconButton>
              )}
              <Box sx={{ flex: 1 }}>
                {wizardStep === 'name' && t('regLanding.loadFromPastPickName', 'Pick a name')}
                {wizardStep === 'publicId' && `${wizardName} — ${t('regLanding.loadFromPastPickId', 'Pick an ID')}`}
                {wizardStep === 'divCat' && `${wizardName} · ${wizardPublicId} — ${t('regLanding.loadFromPastPickDivCat', 'Pick division & category')}`}
                {wizardStep === 'final' && `${wizardName} · ${wizardPublicId} — ${t('regLanding.loadFromPastPickReg', 'Pick a registration')}`}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
                {wizardStep === 'name' && uniqueNames.map((name) => (
                  <Button
                    key={name}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => {
                      setWizardName(name);
                      const ids = Array.from(new Set(filtered.filter((r) => r.name === name).map((r) => displayPublicId(r.publicId))));
                      if (ids.length === 1) {
                        setWizardPublicId(ids[0]);
                        const divCats = new Set();
                        filtered.filter((r) => r.name === name && displayPublicId(r.publicId) === ids[0]).forEach((r) => {
                          divCats.add([r.division, r.category, r['power-factor']].join('|'));
                        });
                        if (divCats.size === 1) {
                          setWizardDivCat([...divCats][0]);
                          setWizardStep('final');
                        } else {
                          setWizardStep('divCat');
                        }
                      } else {
                        setWizardStep('publicId');
                      }
                    }}
                  >
                    {name}
                  </Button>
                ))}

                {wizardStep === 'publicId' && uniquePublicIds.map((pid) => (
                  <Button
                    key={pid}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => {
                      setWizardPublicId(pid);
                      const divCats = new Set();
                      regsForName.filter((r) => displayPublicId(r.publicId) === pid).forEach((r) => {
                        divCats.add([r.division, r.category, r['power-factor']].join('|'));
                      });
                      if (divCats.size === 1) {
                        setWizardDivCat([...divCats][0]);
                        setWizardStep('final');
                      } else {
                        setWizardStep('divCat');
                      }
                    }}
                  >
                    {pid}
                  </Button>
                ))}

                {wizardStep === 'divCat' && uniqueDivCats.map(({ key, division, category, 'power-factor': pf }) => {
                  const divKey = toDivisionKey(division);
                  const catKey = toCategoryKey(category);
                  return (
                    <Button
                      key={key}
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        setWizardDivCat(key);
                        const regs = regsForPublicId.filter((r) => [r.division, r.category, r['power-factor']].join('|') === key);
                        const seen = new Set();
                        const uniques = [];
                        regs.forEach((r) => {
                          const k = matchExtraFields.map((f) => {
                            const fk = (typeof f === 'string' ? f : f?.key || '').replace(/_optional$/, '');
                            return `${fk}=${(r.extraFields || {})[fk] || ''}`;
                          }).join('||');
                          const fullKey = [r.club || '', r.email || '', k].join('||');
                          if (seen.has(fullKey)) return;
                          seen.add(fullKey);
                          uniques.push(r);
                        });
                        if (uniques.length === 1) {
                          loadFromPast(uniques[0]);
                        } else {
                          setWizardStep('final');
                        }
                      }}
                    >
                      {[t(`divisions.${divKey}`), t(`categories.${catKey}`), pf].filter(Boolean).join(' · ')}
                    </Button>
                  );
                })}

                {wizardStep === 'final' && finalUniqueRegs.map((reg, i) => {
                  const extraFieldsStr = matchExtraFields.map((f) => {
                    const fk = (typeof f === 'string' ? f : f?.key || '').replace(/_optional$/, '');
                    const val = (reg.extraFields || {})[fk];
                    return val ? `${fk}: ${val}` : null;
                  }).filter(Boolean).join(' · ');
                  return (
                    <Button
                      key={i}
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={() => loadFromPast(reg)}
                    >
                      {[reg.club, reg.email, extraFieldsStr].filter(Boolean).join(' · ') || t('regLanding.loadFromPastUseReg', 'Use this registration')}
                    </Button>
                  );
                })}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => resetWizard(null)}>{t('common.cancel')}</Button>
            </DialogActions>
          </Dialog>
        </>
        );
      })()}

      {$match.registrationInstructions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box dangerouslySetInnerHTML={{ __html: $match.registrationInstructions }} />
        </Alert>
      )}

      <ShooterForm
        ref={formRef}
        match={$match}
        squadAvailability={squadAvailability}
        editingShooter={editingShooter}
        onSave={handleSave}
        saving={sending}
        showTos
        idLocked={computedIdLocked}
        prefill={authInfo ? { name: authInfo.name, email: authInfo.email } : null}
        gsheetActive={$match.registrationLimitGSheet != null}
        ro={ro}
      />

      <Snackbar
        open={!!saveError}
        message={saveError}
        severity="error"
        onClose={() => setSaveError(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Registration component – orchestrates landing vs form
// ---------------------------------------------------------------------------
export default function Registration({ ro = false }) {
  const { t } = useTranslation();
  const { match } = useMatch();
  const { user, signIn, signUp, signOut, forceChangePassword, forgotPassword, confirmForgotPassword } = useAuth();
  const [view, setView] = useState('landing'); // 'landing' | 'myRegistrations' | 'register'
  const [authInfo, setAuthInfo] = useState(null); // { name, email, provider, shooterId? }
  const [editingShooter, setEditingShooter] = useState(null);
  const [pinError, setPinError] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeError, setCodeError] = useState(false);

  // If user is already logged in via AuthContext, skip landing
  useEffect(() => {
    if (user) {
      setAuthInfo({ name: user.name, email: user.email, provider: 'cognito' });
      setView(ro ? 'register' : 'myRegistrations');
    }
  }, [user, ro]);

  // Handle native app Google login return
  useEffect(() => {
    const nativeToken = sessionStorage.getItem('nativeAuthToken');
    if (!nativeToken) return;
    sessionStorage.removeItem('nativeAuthToken');
    sessionStorage.removeItem('nativeAuthName');
    try {
      const idToken = nativeToken.startsWith('com.google|') ? nativeToken.slice('com.google|'.length) : nativeToken;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      setAuthInfo({
        name: payload.name,
        email: payload.email,
        provider: 'google',
        token: idToken,
      });
      setView(ro ? 'register' : 'myRegistrations');
    } catch { /* ignore */ }
  }, [ro]);

  if (!match) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (ro && match.roRegistration !== 'open' && match.roRegistration !== 'open-code') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="info">{t('regLanding.roRegistrationClosed', 'RO registration is closed for this match')}</Alert>
      </Box>
    );
  }

  if (!ro && (match.registration === 'closed' || match.registration === 'sold-out' || match.registration === 'period-over')) {
    const messageKey = match.registration === 'sold-out'
      ? 'regLanding.registrationSoldOut'
      : match.registration === 'period-over'
        ? 'regLanding.registrationPeriodOver'
        : 'regLanding.registrationClosed';
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="info">{t(messageKey)}</Alert>
      </Box>
    );
  }

  const verifyCode = async (code) => {
    const expected = (ro ? match.roRegistrationCode : match.registrationCode)?.value;
    if (!expected) return false;
    const encoder = new TextEncoder();
    const data = encoder.encode(code);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashHex = Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex === expected;
  };

  const needsCode = ro
    ? match.roRegistration === 'open-code'
    : match.registration === 'open-code';

  if (needsCode && !codeVerified) {
    const handleCodeSubmit = async () => {
      if (await verifyCode(registrationCode)) {
        setCodeVerified(true);
      } else {
        setCodeError(true);
      }
    };
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, px: 2, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>{t('regLanding.codeRequired')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('regLanding.codeRequiredDesc')}
        </Typography>
        <TextField
          label={t('regLanding.accessCode')}
          value={registrationCode}
          onChange={(e) => { setRegistrationCode(e.target.value); setCodeError(false); }}
          error={codeError}
          helperText={codeError ? t('regLanding.codeIncorrect') : ''}
          fullWidth
          sx={{ mb: 2, direction: 'ltr' }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCodeSubmit(); }}
        />
        <Button variant="contained" fullWidth onClick={handleCodeSubmit}>
          {t('regLanding.enterCode')}
        </Button>
      </Box>
    );
  }

  const handleLoginGoogle = (response) => {
    if (response?.credential) {
      try {
        const payload = JSON.parse(atob(response.credential.split('.')[1]));
        setAuthInfo({
          name: payload.name,
          email: payload.email,
          provider: 'google',
          token: response.credential,
        });
        setView(ro ? 'register' : 'myRegistrations');
      } catch {
        setView(ro ? 'register' : 'myRegistrations');
      }
    } else {
      setView('register');
    }
  };

  const handleLoginCognito = async ({ email, password }) => {
    await signIn({ email, password });
    // AuthContext will update `user`, triggering the useEffect above
  };

  const handleForceChangePassword = async ({ email, newPassword, session }) => {
    await forceChangePassword({ email, newPassword, session });
    // AuthContext will update `user`, triggering the useEffect above
  };

  const handleForgotPassword = async ({ email }) => {
    await forgotPassword({ email });
  };

  const handleConfirmForgotPassword = async ({ email, code, newPassword }) => {
    await confirmForgotPassword({ email, code, newPassword });
    // AuthContext will update `user`, triggering the useEffect above
  };

  const handleSignUpCognito = async ({ email, password }) => {
    await signUp({ email, password, name: email });
  };

  const handleManagePin = async ({ countryCode, publicId, pin: pinValue }) => {
    try {
      const fullPublicId = countryCode ? `${countryCode}|${publicId}` : publicId;
      const res = await api.verifyPin(match.id, { publicId: fullPublicId, pin: pinValue });
      const shooters = res.data || [];
      if (shooters.length > 0) {
        const shooter = shooters[0];
        setAuthInfo({
          name: shooter.name,
          email: shooter.email,
          provider: 'pin',
          shooterId: shooter.id,
          publicId: fullPublicId,
          pin: pinValue,
        });
        setView(ro ? 'register' : 'myRegistrations');
      } else {
        setPinError(true);
      }
    } catch {
      setPinError(true);
    }
  };

  if (pinError) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 4, textAlign: 'center', px: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('regLanding.incorrectPin')}
        </Typography>
        <Button variant="contained" onClick={() => { setPinError(false); setView('landing'); }}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  if (view === 'landing') {
    return (
      <RegistrationLanding
        t={t}
        match={match}
        onRegisterNoAuth={() => setView('register')}
        onLoginGoogle={handleLoginGoogle}
        onLoginCognito={handleLoginCognito}
        onSignUpCognito={handleSignUpCognito}
        onManagePin={handleManagePin}
        onForceChangePassword={handleForceChangePassword}
        onForgotPassword={handleForgotPassword}
        onConfirmForgotPassword={handleConfirmForgotPassword}
      />
    );
  }

  if (view === 'myRegistrations') {
    return (
      <MyRegistrations
        t={t}
        match={match}
        authInfo={authInfo}
        onNewRegistration={() => { setEditingShooter(null); setView('register'); }}
        onEditRegistration={(shooter) => { setEditingShooter(shooter); setView('register'); }}
        onLogout={() => { signOut(); setView('landing'); setAuthInfo(null); setEditingShooter(null); }}
      />
    );
  }

  return (
    <RegistrationForm
      t={t}
      match={match}
      authInfo={authInfo}
      editingShooter={editingShooter}
      registrationCode={registrationCode || undefined}
      ro={ro}
      onBack={authInfo ? () => { setEditingShooter(null); setView(ro ? 'landing' : 'myRegistrations'); } : () => { setView('landing'); setAuthInfo(null); }}
    />
  );
}
